import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { FaClock, FaEnvelope } from 'react-icons/fa';
import { theme } from '../../styles/theme';

// Page Header
const PageHeader = styled.div`
  background: linear-gradient(to right, rgba(0, 0, 0, 0.7), rgba(0, 0, 0, 0.7)), 
              url('/images/products-bg.jpg') no-repeat center center;
  background-size: cover;
  height: 40vh;
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 0 24px;
  margin-top: 80px;
  
  @media screen and (max-width: 480px) {
    height: 30vh;
  }
`;

const PageTitle = styled.h1`
  color: ${theme.colors.secondary};
  font-size: 3rem;
  text-align: center;
  max-width: 1200px;
  width: 100%;
  
  @media screen and (max-width: 768px) {
    font-size: 2.5rem;
  }
  
  @media screen and (max-width: 480px) {
    font-size: 2rem;
  }
`;

// Section Container
const SectionContainer = styled.div`
  padding: 100px 0;
  background: ${theme.colors.primary};
  
  @media screen and (max-width: 768px) {
    padding: 70px 0;
  }
  
  @media screen and (max-width: 480px) {
    padding: 50px 0;
  }
`;

const SectionWrapper = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 24px;
  
  @media screen and (max-width: 480px) {
    padding: 0 16px;
  }
`;

const SectionHeading = styled.h2`
  font-size: 2.5rem;
  color: ${theme.colors.secondary};
  margin-bottom: 24px;
  text-align: center;
  
  @media screen and (max-width: 480px) {
    font-size: 2rem;
  }
`;

const SectionSubheading = styled.p`
  font-size: 1.2rem;
  color: ${theme.colors.text.primary};
  margin-bottom: 64px;
  text-align: center;
  max-width: 800px;
  margin-left: auto;
  margin-right: auto;
`;

// Product Card
const ProductGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 32px;
  margin-bottom: 64px;
  
  @media screen and (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 24px;
  }
`;

const ProductCard = styled(motion.div)`
  background: rgba(255, 255, 255, 0.02);
  border: 1px solid rgba(255, 255, 255, 0.05);
  border-radius: ${theme.borderRadius.large};
  padding: 32px;
  transition: all 0.3s ease-in-out;
  
  &:hover {
    transform: translateY(-5px);
    border-color: rgba(212, 175, 55, 0.2);
    box-shadow: ${theme.shadows.large};
  }
`;

const ProductName = styled.h3`
  font-size: 1.5rem;
  color: ${theme.colors.secondary};
  margin-bottom: 12px;
  font-weight: ${theme.typography.fontWeight.bold};
`;

const ProductTagline = styled.p`
  font-size: 1rem;
  color: ${theme.colors.text.primary};
  margin-bottom: 16px;
  line-height: 1.5;
`;

const ComingSoonBadge = styled.div`
  display: inline-flex;
  align-items: center;
  background: rgba(212, 175, 55, 0.2);
  color: ${theme.colors.secondary};
  padding: 8px 16px;
  border-radius: ${theme.borderRadius.pill};
  font-size: 0.9rem;
  font-weight: ${theme.typography.fontWeight.medium};
  margin-top: 16px;
`;

const BadgeIcon = styled(FaClock)`
  margin-right: 8px;
`;

// CTA Section
const CTASection = styled.div`
  background: linear-gradient(135deg, rgba(212, 175, 55, 0.1), rgba(184, 115, 51, 0.05));
  border: 1px solid rgba(212, 175, 55, 0.2);
  border-radius: ${theme.borderRadius.large};
  padding: 48px;
  text-align: center;
  margin-top: 64px;
`;

const CTAHeading = styled.h3`
  font-size: 2rem;
  color: ${theme.colors.secondary};
  margin-bottom: 16px;
`;

const CTADescription = styled.p`
  font-size: 1.1rem;
  color: ${theme.colors.text.primary};
  margin-bottom: 32px;
  max-width: 600px;
  margin-left: auto;
  margin-right: auto;
`;

const CTAButton = styled.a`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: ${theme.colors.secondary};
  color: ${theme.colors.text.secondary};
  padding: 14px 32px;
  border-radius: ${theme.borderRadius.pill};
  font-size: 1rem;
  font-weight: ${theme.typography.fontWeight.medium};
  text-decoration: none;
  transition: all 0.3s ease-in-out;
  
  &:hover {
    background: ${theme.colors.tertiary};
    color: ${theme.colors.text.primary};
    transform: translateY(-3px);
    box-shadow: ${theme.shadows.medium};
  }
`;

const ButtonIcon = styled(FaEnvelope)`
  margin-right: 10px;
`;

const Products = () => {
  const products = [
    {
      name: 'DataFind',
      tagline: 'Asset management platform'
    },
    {
      name: 'SA1stWorkers',
      tagline: 'Marketplace for trusted local professionals'
    },
    {
      name: 'Beauty at Home',
      tagline: 'On-demand beauty services'
    },
    {
      name: 'CloseUp News',
      tagline: 'Community news platform'
    }
  ];

  return (
    <>
      <PageHeader>
        <PageTitle>Products (Coming Soon)</PageTitle>
      </PageHeader>
      
      <SectionContainer>
        <SectionWrapper>
          <SectionHeading>Building Something Exciting</SectionHeading>
          <SectionSubheading>
            We're developing innovative software solutions for businesses and consumers. Join our waiting list to be notified when they're ready.
          </SectionSubheading>
          
          <ProductGrid>
            {products.map((product, index) => (
              <ProductCard
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ 
                  duration: 0.5, 
                  delay: index * 0.1,
                  ease: [0.6, 0.05, 0.01, 0.99] 
                }}
              >
                <ProductName>{product.name}</ProductName>
                <ProductTagline>{product.tagline}</ProductTagline>
                <ComingSoonBadge>
                  <BadgeIcon />
                  Coming Soon
                </ComingSoonBadge>
              </ProductCard>
            ))}
          </ProductGrid>
          
          <CTASection>
            <CTAHeading>Stay Updated</CTAHeading>
            <CTADescription>
              Be the first to know when our products launch. Join our waiting list for early access and exclusive updates.
            </CTADescription>
            <CTAButton href="#contact">
              <ButtonIcon />
              Join Waiting List
            </CTAButton>
          </CTASection>
        </SectionWrapper>
      </SectionContainer>
    </>
  );
};

export default Products;
